/**
 * Add Row facility dedup (OR-1417). Both Regulatory save paths (Acuity
 * occupancies + Surveys) create a SubmissionLocation for a picked catalog
 * facility so it propagates to Schedule. The guard below decides whether a
 * location for that facility ALREADY exists — matching on more than just
 * `facility_id`, because a facility added from the Schedule tab persists no
 * catalog link (`facility_id` comes back null) and would otherwise be
 * invisible, producing a duplicate location on save.
 */
import type { CatalogFacility } from "../../../api/endpoints/facility-catalog";
import type { SubmissionLocationWire } from "../../../types/submission";
import type { AcuityRow } from "./types";

/**
 * The submission location backing a row keyed on the catalog facility.
 *
 * Started here for the Regulatory tables; Operational's staffing rows need the
 * same join for their ⋮ menu, so it now lives in `domain/facility-archive`.
 * Re-exported to keep this module's callers pointing at one name.
 */
export { findFacilityForRow } from "../../../domain/facility-archive";

function norm(value: string | null | undefined): string {
    return (value ?? "").trim().toLowerCase();
}

/** True when an existing submission location already represents this catalog
 *  facility. Matches, in order of confidence:
 *   1. catalog link (`facility_id === cf.id`),
 *   2. CMS number (how the rest of the app joins Acuity↔Schedule; covers
 *      Schedule-created locations whose `facility_id` is null),
 *   3. name + state (last resort when neither side has a CMS number). */
export function locationRepresentsCatalog(loc: SubmissionLocationWire, cf: CatalogFacility): boolean {
    if (loc.facility_id && loc.facility_id === cf.id) return true;
    const locCms = loc.cms_number ?? loc.provider_number ?? null;
    if (locCms && cf.cms_number && locCms === cf.cms_number) return true;
    const cfName = norm(cf.name);
    if (cfName && norm(loc.location_name) === cfName && (loc.state ?? "") === (cf.state ?? "")) return true;
    return false;
}

/** True when an already-loaded Acuity row represents this catalog facility.
 *  Used to warn (and block the save) when Add Row picks a facility that is
 *  already in the table, so its data doesn't silently merge into the existing
 *  row (OR-1417 follow-up). Same matching order as locations: catalog id,
 *  then CMS number (parsed from the row's grouping key — `facilityId|` or
 *  `|cms`), then name + state. */
export function acuityRowMatchesCatalog(row: AcuityRow, cf: CatalogFacility): boolean {
    if (row.facilityId && row.facilityId === cf.id) return true;
    const cmsFromKey = row.facilityKey.split("|")[1] ?? "";
    if (cmsFromKey && cf.cms_number && cmsFromKey === cf.cms_number) return true;
    const cfName = norm(cf.name);
    if (cfName && norm(row.facilityName) === cfName && (row.state ?? "") === (cf.state ?? "")) return true;
    return false;
}
